import I18n from 'i18n-js';

import { useCollectionRanking } from '@lootboy/lib/slices/cardCollections/collectionRanking';
import { hasUserData } from '@lootboy/lib/slices/cardCollections/types';

import { CircleLoader } from '~/components/atoms/CircleLoader';
import { ListContainer } from '~/components/atoms/ListContainer';

import { TUseBooleanFunction } from '~/hooks/useBooleanState';
import { CollectionRankingEntry } from '~/views/CollectionView/CollectionRanking/CollectionRankingEntry';
import { CollectionRankingHeader } from '~/views/CollectionView/CollectionRanking/CollectionRankingHeader';
import { NUMBER_OF_TOP_ENTRIES } from '~/views/CollectionView/CollectionRanking/utils';

import styles from './CollectionRanking.module.scss';

type TCollectionRankingProps = {
  collectionId: string;
  collectionValue: number;
  openMethods: {
    readonly infoDialog: TUseBooleanFunction;
    readonly topRankingDialog: TUseBooleanFunction;
    readonly userRankingDialog: TUseBooleanFunction;
    readonly rankingDesyncDialog: TUseBooleanFunction;
  };
};

export function CollectionRanking({
  collectionId,
  collectionValue,
  openMethods,
}: TCollectionRankingProps): JSX.Element {
  const ranking = useCollectionRanking(collectionId);
  const isRankingPopulated = ranking?.top?.length;

  if (!isRankingPopulated) {
    return <></>;
  }

  const topRankingSection = ranking.top.slice(0, NUMBER_OF_TOP_ENTRIES);

  const userRanking = ranking && hasUserData(ranking) && ranking.rank;
  const isUserTopRanked =
    userRanking && ranking.top?.some((entry) => entry.isUserEntry);
  const isUserInTopSection =
    userRanking && topRankingSection.some((entry) => entry.isUserEntry);

  return (
    <section className={styles.rankingSection}>
      <CollectionRankingHeader
        onIconClick={openMethods.infoDialog}
        onShowMoreClick={openMethods.topRankingDialog}
      />
      {!ranking ? (
        <div className={styles.loaderContainer}>
          <CircleLoader size={75} />
        </div>
      ) : (
        <>
          <ListContainer className={styles.rankingContainer}>
            {topRankingSection.map((rankingEntry) => (
              <CollectionRankingEntry
                rankingEntry={rankingEntry}
                onClick={openMethods.topRankingDialog}
                onPositionIconClick={
                  isUserInTopSection &&
                  rankingEntry.isUserEntry &&
                  collectionValue !== ranking.rank?.value
                    ? openMethods.rankingDesyncDialog
                    : undefined
                }
                key={`${rankingEntry.username}_${rankingEntry.no}`}
              />
            ))}
          </ListContainer>
          <div className={styles.userRanking}>
            <p className={styles.userRankingInfo}>
              {I18n.t('common_your_rank')}
            </p>
            {userRanking ? (
              <CollectionRankingEntry
                onClick={
                  isUserTopRanked
                    ? openMethods.topRankingDialog
                    : openMethods.userRankingDialog
                }
                onPositionIconClick={
                  collectionValue !== ranking.rank?.value
                    ? openMethods.rankingDesyncDialog
                    : undefined
                }
                rankingEntry={userRanking}
              />
            ) : (
              <p className={styles.userRankingPlaceholder}>
                {I18n.t('collections_ranking_placeholder')}
              </p>
            )}
          </div>
        </>
      )}
    </section>
  );
}
